import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ID_ROUTE_PARAM } from '@core/constants/navigation/navigation-params.const';
import { ComponentHelper } from '@shared/helpers/component.helper';
import { ResponseDefault, ResponseError } from '@shared/interfaces/http.interface';
import { AnyObject } from '@shared/interfaces/objects.interface';
import { FormFields, FormGroup as FormGroupInterface } from '@shared/modules/form/interfaces';

export abstract class FormHelper extends ComponentHelper {
  public id: string;
  public isFormReady: boolean;
  public formGroup: UntypedFormGroup;
  public formFields: FormFields;
  public panelTitle: string;
  public pageTitle: string;

  protected setTitles(): void {
    if (this.isEditMode) {
      this.panelTitle = 'Formularz edycji';
      this.pageTitle = 'Edycja';
    } else {
      this.panelTitle = 'Formularz tworzenia';
      this.pageTitle = 'Tworzenie';
    }
  }

  setFormGroup(formGroup: FormGroupInterface, formFields: FormFields): void {
    this.setFormGroupConfig(formGroup);
    this.setFormFields(formFields);
  }

  setFormGroupConfig(formGroupConfig: FormGroupInterface): void {
    this.formGroup = this.service.formBuilder.group(formGroupConfig.controls, formGroupConfig.options);
  }

  setFormFields(fields: FormFields): void {
    this.formFields = fields;
  }

  isFormGroupStatus(flag: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.formGroup[flag] ? resolve(true) : reject();
    });
  }

  isFormGroupValid(showError: boolean = true): boolean {
    const {valid} = this.formGroup;

    if (showError && !valid) {
      this.service.toastr.error('Wypełnij prawidłowo wszystkie pola formularza.');
    }

    return valid;
  }

  isFormControl(field: string): boolean {
    return !!this.formGroup && !!this.getFormControl(field);
  }

  isFormControlError(field: string): boolean {
    return !!this.formGroup && !!this.getFormControlErrors(field);
  }

  getFormControl(field: string): UntypedFormControl {
    return this.formGroup?.get?.(field) as UntypedFormControl;
  }

  getFormControlValue(field: string): any {
    if (this.isFormControl(field)) {
      return this.getFormControl(field).value;
    }
    return undefined;
  }

  getFormControlRawValue(field: string): any {
    if (this.isFormControl(field)) {
      return this.formGroup.getRawValue()[field];
    }
    return undefined;
  }

  getFormArray(field: string): UntypedFormArray {
    return this.formGroup.get(field) as UntypedFormArray;
  }

  getFormArrayFormField(key: string, index: number): FormFields {
    return {
      ...this.formFields[key],
      id: `${this.formFields[key].id}--${index}`,
    };
  }

  getFormControlErrors(field: string): ValidationErrors | null {
    if (this.isFormControl(field) && this.getFormControl(field).errors) {
      return this.getFormControl(field).errors;
    }
    return null;
  }

  setFormControlStatus(field: string, status: boolean): void {
    if (this.isFormControl(field)) {
      if (status) {
        this.getFormControl(field).enable();
      } else {
        this.getFormControl(field).disable();
      }
    }
  }

  setFormGroupControlTouched(key: string): void {
    this.getFormControl(key).markAsTouched();
  }

  setAllControlsAsTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  setFormGroupControlError(key: string, error: AnyObject): void {
    this.getFormControl(key).setErrors(error);
  }

  setApiError(errors: AnyObject = {}): void {
    const errorsKeys: string[] = Object.keys(errors) || [];

    if (errorsKeys.length) {
      for (const key of errorsKeys) {
        if (this.getFormControl(key) instanceof UntypedFormArray) {
          // Obsługa błędów dla FormArray.
          this.setFormArrayErrorsInFormGroup(key, errors);
        } else {
          // Obsługa błędów dla FormGroup.
          this.setFormControlStatus(key, true);
          this.setFormGroupControlTouched(key);
          this.setFormGroupControlError(key, { api: errors[key] });
        }
      }
    }
  }

  /**
   * Ustawianie błędów dla każdego zagnieżdzonego FormControl, który posiada dany FormArray
   * Ważne, aby FormArray, posiadał kolekcję FormGroup, który z kolei posiada FormControl(s)
   * Bezpośrednie umieszczenie FormControl (z pominięciem FormGroup) w FormArray, tutaj nie zadziała.
   * @param key - klucz w FormGroup'ie, który zawiera FormArray
   * @param errors - błędy w postaci np. { organizational_units_ratings: [{ year: 'To pole powiinno być wymagane' }]}
   */
  private setFormArrayErrorsInFormGroup(key: string, errors: AnyObject = {}): void {
    const indexes = Object.keys(errors[key]);
    indexes.forEach(index => {
      const errorParams = Object.keys(errors[key][index]);
      errorParams.forEach(param => {
        const control: UntypedFormControl = this.getFormArray(key).at(+index).get(param) as UntypedFormControl;
        control.markAsTouched();
        control.setErrors({ api: errors[key][index][param]});
      });
    });
  }

  protected setID(id: string): void {
    this.id = id;
  }

  protected setIDFromURL(route: ActivatedRoute): void {
    this.setID(route.snapshot.params[ID_ROUTE_PARAM]);
  }

  public get isEditMode(): boolean {
    return !!this.id;
  }

  public onResponseSaved(response: ResponseDefault, reset: boolean = true): Promise<null> {
    return new Promise<null>((resolve) => {
      return super.onResponseSaved(response)
        .then(() => {
          if (reset) {
           this.formGroup.reset();
          }
          resolve(null);
        });
    });
  }

  public onResponseSavedError(error: ResponseError = ({} as ResponseError)): Promise<null> {
    return new Promise<null>((resolve) => {
      return super.onResponseSavedError(error)
        .then(() => {
          this.setApiError(error.errors);
          resolve(null);
        });
    });
  }

  public onFormGroupValueChange(key: string, value: any): void {
    this.formGroup.patchValue({ [key]: value });
  }

  public onFormGroupValuesChange(values: AnyObject) {
    this.formGroup.patchValue(values);
  }

  protected onFormRefresh(): void {
    this.formGroup.updateValueAndValidity();
  }

  protected onFormGroupReset(value?: any, options?: {
    onlySelf?: boolean;
    emitEvent?: boolean;
  }): void {
    this.formGroup.reset(value, options);
  }
}
